import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ApiService from '../../api/ApiService';
import LocalDataService from '../../api/LocalDataService';
import {
  formatTaskPoints,
  formatTaskTime,
  getTaskModelName,
  getTaskPreview,
  getTaskPrompt,
  getTaskStatusMeta
} from '../../utils/taskRecord';
import './TaskRecordsPage.css';

const PAGE_SIZE = 12;

const STATUS_FILTERS = [
  { label: '全部', value: '' },
  { label: '已完成', value: 'completed' },
  { label: '处理中', value: 'pending,processing' },
  { label: '失败', value: 'failed,webhook_error' }
];

function TaskRecordsPage() {
  const user = LocalDataService.load_user_data();
  const requestVersion = useRef(0);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeStatus, setActiveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const loadTasks = useCallback(async (nextPage = 1, append = false) => {
    if (!user?.token) return;
    const version = ++requestVersion.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError('');

    try {
      const response = await new ApiService().get_fal_task_list(
        user.token,
        nextPage,
        PAGE_SIZE,
        '',
        activeStatus,
        { silent: true }
      );
      if (version !== requestVersion.current) return;
      if (response?.code !== 200) throw new Error('任务记录加载失败');

      const nextTasks = Array.isArray(response?.data?.list) ? response.data.list : [];
      setTasks((current) => (append ? [...current, ...nextTasks] : nextTasks));
      setTotal(Number(response?.data?.total) || 0);
      setPage(nextPage);
    } catch (_) {
      if (version === requestVersion.current) setError('任务记录加载失败，请稍后重试');
    } finally {
      if (version === requestVersion.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [activeStatus, user?.token]);

  useEffect(() => {
    setTasks([]);
    setTotal(0);
    setPage(1);
    loadTasks(1, false);
  }, [loadTasks]);

  const hasMore = tasks.length < total;

  return (
    <main className="TaskRecordsPage-root">
      <section className="TaskRecordsPage-hero">
        <div className="TaskRecordsPage-hero-inner">
          <Link className="TaskRecordsPage-back" to="/">
            <span aria-hidden="true">←</span> 返回灵感画布
          </Link>
          <span className="TaskRecordsPage-kicker">TASK ARCHIVE · CREATION HISTORY</span>
          <div className="TaskRecordsPage-title-row">
            <div>
              <h1>任务记录</h1>
              <p>查看本账号的生成任务、执行状态与积分消耗</p>
            </div>
            <div className="TaskRecordsPage-total">
              <span>全部任务</span>
              <strong>{total.toLocaleString('zh-CN')}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="TaskRecordsPage-content">
        <div className="TaskRecordsPage-toolbar">
          <div className="TaskRecordsPage-filters" aria-label="任务状态筛选">
            {STATUS_FILTERS.map((filter) => (
              <button
                className={activeStatus === filter.value ? 'is-active' : ''}
                type="button"
                key={filter.value}
                aria-pressed={activeStatus === filter.value}
                onClick={() => setActiveStatus(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <span className="TaskRecordsPage-sync"><i /> 与任务中心实时同步</span>
        </div>

        {isLoading && tasks.length === 0 && (
          <div className="TaskRecordsPage-state" aria-live="polite">
            <i className="TaskRecordsPage-spinner" />
            <strong>正在读取任务记录</strong>
            <span>请稍候</span>
          </div>
        )}

        {!isLoading && error && (
          <div className="TaskRecordsPage-state is-error" role="alert">
            <strong>{error}</strong>
            <button type="button" onClick={() => loadTasks(1, false)}>重新加载</button>
          </div>
        )}

        {!isLoading && !error && tasks.length === 0 && (
          <div className="TaskRecordsPage-state">
            <span className="TaskRecordsPage-empty-mark">AI</span>
            <strong>暂无任务记录</strong>
            <span>完成一次创作后，任务会自动出现在这里</span>
          </div>
        )}

        {tasks.length > 0 && (
          <div className="TaskRecordsPage-grid">
            {tasks.map((task) => {
              const preview = getTaskPreview(task);
              const statusMeta = getTaskStatusMeta(task.status);
              const prompt = getTaskPrompt(task);
              const taskId = String(task.task_id || task.id || '');

              return (
                <article className="TaskRecordsPage-card" key={task.id || task.task_id}>
                  <div className="TaskRecordsPage-card-header">
                    <div>
                      <strong title={task.app_name}>{getTaskModelName(task.app_name)}</strong>
                      <span title={taskId}>{taskId ? `#${taskId.slice(-12)}` : '任务 ID 未知'}</span>
                    </div>
                    <span className={`TaskRecordsPage-status ${statusMeta.className}`}>
                      <i /> {statusMeta.label}
                    </span>
                  </div>

                  <div className={`TaskRecordsPage-media${preview ? '' : ' is-empty'}`}>
                    {preview?.type === 'image' && <img src={preview.url} alt={`${getTaskModelName(task.app_name)} 输出`} loading="lazy" />}
                    {preview?.type === 'video' && <video src={preview.url} controls preload="metadata" playsInline />}
                    {preview?.type === 'audio' && (
                      <div className="TaskRecordsPage-audio">
                        <span aria-hidden="true">♪</span>
                        <audio src={preview.url} controls preload="none" />
                      </div>
                    )}
                    {!preview && (
                      <div className="TaskRecordsPage-media-placeholder">
                        <span>{task.status === 'failed' || task.status === 'webhook_error' ? '!' : 'AI'}</span>
                        <small>{statusMeta.label}</small>
                      </div>
                    )}
                  </div>

                  <div className="TaskRecordsPage-prompt" title={prompt || '暂无提示词'}>
                    {prompt || '该任务未记录提示词'}
                  </div>

                  <footer className="TaskRecordsPage-card-footer">
                    <strong>{formatTaskPoints(task.money)} <small>积分</small></strong>
                    <time dateTime={task.created_at || ''}>{formatTaskTime(task.created_at, true)}</time>
                  </footer>
                </article>
              );
            })}
          </div>
        )}

        {tasks.length > 0 && (
          <div className="TaskRecordsPage-pagination">
            {hasMore ? (
              <button type="button" disabled={isLoadingMore} onClick={() => loadTasks(page + 1, true)}>
                {isLoadingMore ? '正在加载…' : '加载更多任务'}
              </button>
            ) : (
              <span>已展示全部任务</span>
            )}
          </div>
        )}
      </section>
    </main>
  );
}

export default TaskRecordsPage;
